import type { LocalDatabase } from '../core/types';
import type {
  ChatRecord,
  ChatMessageRecord,
  ChatMessagePartRecord,
} from '~/powersync/generated/schema';
import { RandomUtils } from '@fytness/shared';

/**
 * Fallback chat title used when the caller doesn't pass a localized one. The UI
 * normally supplies the translated `chat:list.defaultName`.
 */
export const DEFAULT_CHAT_NAME = 'Chat session';

export const ChatMutations = {
  /**
   * Get the active chat or create a new one if it doesn't exist
   * Note: We don't filter by userId because it's populated on backend after sync
   * Instead we just get the most recent non-deleted chat
   */
  getOrCreateChat: async (
    db: LocalDatabase,
    userId: string,
    name: string = DEFAULT_CHAT_NAME,
  ): Promise<ChatRecord> => {
    // Check if an active chat exists (any chat for this device)
    // Don't filter by userId since it's null until backend sync
    const activeChat = await db
      .selectFrom('Chat')
      .selectAll()
      .where('deletedAt', 'is', null)
      .orderBy('createdAt', 'desc')
      .limit(1)
      .executeTakeFirst();

    if (activeChat) {
      return activeChat;
    }

    // Create a new chat if none exists
    return ChatMutations.create(db, userId, name);
  },

  /**
   * Create a new chat. `name` is NOT NULL on the backend, so we always seed it
   * with the (localized) "Chat session" default; the AI worker overwrites it
   * with a generated title once the first message is sent.
   *
   * `context`/`contextRefId` route the conversation to the right AI graph
   * (core-engine reads them off the synced Chat row). Omit them for a GENERAL
   * chat; pass them to scope a chat to a workout / meal day.
   */
  create: async (
    db: LocalDatabase,
    userId: string,
    name: string = DEFAULT_CHAT_NAME,
    options: { context?: string | null; contextRefId?: string | null } = {},
  ): Promise<ChatRecord> => {
    const id = RandomUtils.uuid();
    const now = new Date().toISOString();

    const chat: ChatRecord = {
      id,
      // Populated on the backend after sync (see getOrCreateChat rationale).
      userId: null,
      niceId: null,
      name,
      context: options.context ?? null,
      contextRefId: options.contextRefId ?? null,
      createdAt: now,
      updatedAt: now,
      deletedAt: null,
    };

    await db.insertInto('Chat').values(chat).execute();

    return chat;
  },

  /**
   * Get the existing chat for a routing context (e.g. WORKOUT + workout id), or
   * create one scoped to it. Keeps each workout / meal-day conversation distinct
   * from the global GENERAL chat. Not filtered by userId for the same reason as
   * {@link getOrCreateChat} (locally-created rows have a null userId until sync).
   */
  getOrCreateForContext: async (
    db: LocalDatabase,
    context: string,
    contextRefId: string,
    name: string = DEFAULT_CHAT_NAME,
  ): Promise<ChatRecord> => {
    const existing = await db
      .selectFrom('Chat')
      .selectAll()
      .where('context', '=', context)
      .where('contextRefId', '=', contextRefId)
      .where('deletedAt', 'is', null)
      .orderBy('createdAt', 'desc')
      .limit(1)
      .executeTakeFirst();

    if (existing) {
      return existing;
    }

    return ChatMutations.create(db, '', name, { context, contextRefId });
  },

  /**
   * Soft delete a chat
   */
  softDelete: async (db: LocalDatabase, id: string): Promise<void> => {
    const now = new Date().toISOString();
    await db
      .updateTable('Chat')
      .set({
        deletedAt: now,
        updatedAt: now,
      })
      .where('id', '=', id)
      .execute();
  },

  /**
   * Create a new chat message with a text part
   * Note: sender is always HUMAN (only humans can create messages from client)
   * Note: userId is populated automatically on backend
   */
  createMessage: async (
    db: LocalDatabase,
    chatId: string,
    text: string,
  ): Promise<ChatMessageRecord> => {
    return await db.transaction().execute(async (trx) => {
      const messageId = RandomUtils.uuid();
      const partId = RandomUtils.uuid();
      const now = new Date().toISOString();

      const message: ChatMessageRecord = {
        id: messageId,
        chatId,
        sender: 'HUMAN',
        createdAt: now,
        userId: null,
        metadata: null,
      };

      const part: ChatMessagePartRecord = {
        id: partId,
        messageId,
        chatId,
        userId: null,
        order: 0,
        type: 'text',
        text,
        kind: null,
        payloadJson: null,
        createdAt: now,
      };

      // Insert message and part atomically
      await trx.insertInto('ChatMessage').values(message).execute();
      await trx.insertInto('ChatMessagePart').values(part).execute();

      // Update chat's updatedAt timestamp
      await trx
        .updateTable('Chat')
        .set({ updatedAt: now })
        .where('id', '=', chatId)
        .execute();

      return message;
    });
  },
};
